import type { Module } from "vuex";
import type { DefaultApi, WeeklyStatisticsResponse } from "@/api";
import { Statistics } from "@/models/statistics";
const makeStatisticsKey = (
  before?: string | null,
  after?: string | null,
  companyIds?: number[],
  positionIds?: number[],
): string => {
  const c = companyIds?.length ? [...companyIds].sort().join(",") : "null";
  const p = positionIds?.length ? [...positionIds].sort().join(",") : "null";
  return `${before ?? "null"}::${after ?? "null"}::${c}::${p}`;
};
export interface State {
  statisticsCache: Record<string, Statistics>;
  weeklyStatisticsCache: Record<string, WeeklyStatisticsResponse>;
}
const pendingStatistics = new WeakMap<
  State,
  Map<string, Promise<Statistics>>
>();

export const module: Module<State, any> = {
  namespaced: true,
  state: {
    statisticsCache: {},
    weeklyStatisticsCache: {},
  },
  getters: {
    getStatistics:
      (state) =>
      (
        before?: string | null,
        after?: string | null,
        companyIds?: number[],
        positionIds?: number[],
      ): Statistics | undefined => {
        const key = makeStatisticsKey(before, after, companyIds, positionIds);
        return state.statisticsCache[key];
      },
    getWeeklyStatistics:
      (state) =>
      (
        before?: string | null,
        after?: string | null,
        companyIds?: number[],
        positionIds?: number[],
      ): WeeklyStatisticsResponse | undefined => {
        const key = makeStatisticsKey(before, after, companyIds, positionIds);
        return state.weeklyStatisticsCache[key];
      },
  },
  actions: {
    async getStatistics(
      { state, commit },
      {
        before,
        after,
        companyIds,
        positionIds,
        useCache = false,
      }: {
        before?: string | null;
        after?: string | null;
        companyIds?: number[];
        positionIds?: number[];
        useCache?: boolean;
      } = {},
    ) {
      const key = makeStatisticsKey(before, after, companyIds, positionIds);
      let pending = pendingStatistics.get(state);
      if (!pending) {
        pending = new Map();
        pendingStatistics.set(state, pending);
      }
      const existing = pending.get(key);
      if (useCache && existing) return existing;
      if (useCache && state.statisticsCache[key])
        return state.statisticsCache[key];

      const request = Promise.resolve().then(async () => {
        const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
          root: true,
        });
        const response = await api.getStatistics(
          before,
          after,
          companyIds?.length ? companyIds : undefined,
          positionIds?.length ? positionIds : undefined,
        );
        const statistics = new Statistics(response.data);
        if (pending.get(key) === request)
          commit("setStatistics", {
            statistics,
            before,
            after,
            companyIds,
            positionIds,
          });
        return statistics;
      });
      pending.set(key, request);
      try {
        return await request;
      } finally {
        if (pending.get(key) === request) pending.delete(key);
      }
    },
    async getWeeklyStatistics(
      { commit },
      {
        before,
        after,
        companyIds,
        positionIds,
      }: {
        before?: string | null;
        after?: string | null;
        companyIds?: number[];
        positionIds?: number[];
      } = {},
    ) {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.getWeeklyStatistics(
        before,
        after,
        companyIds?.length ? companyIds : undefined,
        positionIds?.length ? positionIds : undefined,
      );
      const weeklyStatistics = response.data;
      commit("setWeeklyStatistics", {
        weeklyStatistics,
        before,
        after,
        companyIds,
        positionIds,
      });
      return weeklyStatistics;
    },
  },
  mutations: {
    setStatistics(
      state,
      payload: {
        statistics: Statistics;
        before?: string | null;
        after?: string | null;
        companyIds?: number[];
        positionIds?: number[];
      },
    ) {
      const key = makeStatisticsKey(
        payload.before,
        payload.after,
        payload.companyIds,
        payload.positionIds,
      );
      state.statisticsCache[key] = payload.statistics;
    },
    setWeeklyStatistics(
      state,
      payload: {
        weeklyStatistics: WeeklyStatisticsResponse;
        before?: string | null;
        after?: string | null;
        companyIds?: number[];
        positionIds?: number[];
      },
    ) {
      const key = makeStatisticsKey(
        payload.before,
        payload.after,
        payload.companyIds,
        payload.positionIds,
      );
      state.weeklyStatisticsCache[key] = payload.weeklyStatistics;
    },
    invalidateStatistics(state) {
      state.statisticsCache = {};
      pendingStatistics.get(state)?.clear();
      pendingStatistics.delete(state);
    },
  },
};
